import clsx from 'clsx';
import AboutUsModule from 'components/about-us/AboutUsModule';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import AboutUsSiteInnerContainer from 'components/about-us/AboutUsSiteInnerContainer';
import AboutUsSplitHero from 'components/about-us/AboutUsSplitHero';
import EmblaCarousel from 'components/carousel2025/EmblaCarousel';
import { EntryMethod } from 'components/enter/EntryMethod';
import FaqCard from 'components/FaqCard';
import ImageWithFallback from 'components/ImageWithFallback';
import RoundedButton from 'components/RoundedButton';
import useEmblaCarousel from 'embla-carousel-react';
import { useEnterStore } from 'hooks/stores/useEnterStore';
import { useIsMobile } from 'hooks/ui/useIsMobile';
import Link from 'next/link';
import TealCircleCrown from '../components/svg/TealCircleCrown.svg';
import TealCircleNeverMiss from '../components/svg/TealCircleNeverMiss.svg';
import TealCircleTree from '../components/svg/TealCircleTree.svg';
import TealCircleTrophy from '../components/svg/TealCircleTrophy.svg';
import WhiteShadowArrow from '../components/svg/WhiteShadowArrow.svg';
import WhiteShadowMatch from '../components/svg/WhiteShadowMatch.svg';
import WhiteShadowTrophy from '../components/svg/WhiteShadowTrophy.svg';
import styles from './choose-your-plan-original.module.css';
import { faqs } from './choose-your-plan-v1';

interface Props {
  litePrice?: string;
  premiumPrice?: string;
  isBoosted?: boolean;
}

export const ChooseYourPlanOriginal = ({
  litePrice = '£1.50',
  premiumPrice = '£2.95',
  isBoosted = false,
}: Props) => {
  const enterStore = useEnterStore();
  const { isMobile } = useIsMobile();
  const emblaCarouselInfo = useEmblaCarousel({
    loop: false,
    duration: 10,
    inViewThreshold: 0.1,
  });

  return (
    <div className="daymadeTypographyReset">
      <AboutUsModule background="white">
        <AboutUsSiteContainer>
          <AboutUsSiteInnerContainer>
            <div className={styles.header}>
              <div
                className={clsx(
                  styles.title,
                  'aboutUsTypographyHeaderXlargeOnWhite',
                )}
              >
                Choose your plan
              </div>
              <div className={styles.subtitle}>Snooze or cancel anytime</div>
            </div>
            <div className={styles.planChoices}>
              <div className={styles.planChoice}>
                <div className={styles.planTextWrapper}>
                  <div
                    className={clsx(
                      styles.planTitle,
                      'aboutUsTypographyHeaderXlargeOnWhite',
                      'aboutUsTypographyOrangeUnderlineColour',
                    )}
                  >
                    Lite
                  </div>
                  <div className={styles.planSubtitle}>
                    Good chances & many prizes
                  </div>
                </div>
                <div className={styles.planBreaker} />
                <div className={styles.buttonWrapper}>
                  <RoundedButton
                    href="/enter?em=LiteSubscription"
                    rebrandDm2025
                    style={{ fontSize: isMobile ? '13px' : '20px' }}
                    onClick={() => {
                      if (isBoosted) {
                        enterStore.setBoosted(true);
                      }
                    }}
                  >
                    Play from {litePrice}
                  </RoundedButton>
                </div>
              </div>
              <div className={styles.planChoice}>
                <div className={styles.planTextWrapper}>
                  <div
                    className={clsx(
                      styles.planTitle,
                      'aboutUsTypographyHeaderXlargeOnWhite',
                    )}
                  >
                    Premium
                  </div>
                  <div className={styles.planSubtitle}>
                    Best chances & all our prizes
                  </div>
                </div>
                <div className={styles.planBreaker} />
                <div className={styles.buttonWrapper}>
                  <RoundedButton
                    href="/enternow/1"
                    rebrandDm2025
                    style={{ fontSize: isMobile ? '13px' : '20px' }}
                    onClick={() => {
                      enterStore.setEntryMethod(
                        EntryMethod.PremiumSubscription,
                      );
                      if (isBoosted) {
                        enterStore.setBoosted(true);
                      }
                    }}
                  >
                    Play from {premiumPrice}
                  </RoundedButton>

                  <div
                    className={clsx(
                      styles.planBonusOffer,
                      'aboutUsTypographyBodySmall',
                    )}
                  >
                    <strong>🎉50% off first order🎉</strong>
                  </div>
                </div>
              </div>
              <div className={styles.planChoice}>
                <div className={styles.planTextWrapper}>
                  <div
                    className={clsx(
                      styles.planTitle,
                      'aboutUsTypographyHeaderXlargeOnWhite',
                      'aboutUsTypographyPinkUnderlineColour',
                    )}
                  >
                    Postal
                  </div>
                  <div className={styles.planSubtitle}>
                    Play for free by post
                  </div>
                </div>
                <div className={styles.planBreaker} />
                <div className={styles.buttonWrapper}>
                  <RoundedButton
                    href="/alternative-entry-method"
                    rebrandDm2025
                    style={{ fontSize: isMobile ? '13px' : '20px' }}
                  >
                    Play by post
                  </RoundedButton>
                </div>
              </div>
            </div>
            {isMobile && (
              <div className={styles.trustpilot}>
                <Link href="https://uk.trustpilot.com/review/daymade.co.uk">
                  <ImageWithFallback
                    src={`https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/choose-your-plan/trustpilot-green-black-text.svg`}
                    width={140}
                    height={40}
                    alt="Trustpilot"
                    className={styles.trustpilotImage}
                  />
                </Link>
              </div>
            )}
          </AboutUsSiteInnerContainer>
        </AboutUsSiteContainer>
      </AboutUsModule>

      <AboutUsModule background="grey" withNoPadding>
        <AboutUsSplitHero
          background="transparent"
          imageSwitchSide
          imageContainerAbove
          imagePath="https://daymade-website-assets.s3.amazonaws.com/2026/choose-your-plan/exclusive-benefits.png"
          imageAlt="DAYMADE winner on holiday"
        >
          <div className={styles.exclusiveBenefits}>
            <div
              className={clsx(
                'aboutUsTypographyHeaderXlargeOnWhite',
                styles.benefitsTitle,
              )}
            >
              Exclusive Benefits
            </div>
            <div className={styles.benefitsSubtitle}>
              For <strong>Lite</strong> and <strong>Premium</strong> users:
            </div>
            <div className={styles.benefits}>
              <div className={styles.benefit}>
                <TealCircleTree width={60} />
                <div className={styles.benefitText}>
                  <div className={clsx(styles.benefitTitle)}>
                    Plant a tree every week
                  </div>
                  <div
                    className={clsx(
                      styles.benefitSubtitle,
                      'aboutUsTypographyBodySmall',
                    )}
                  >
                    With our friends at Veritree
                  </div>
                </div>
              </div>
              <div className={styles.benefit}>
                <TealCircleNeverMiss width={60} />
                <div className={styles.benefitText}>
                  <div className={clsx(styles.benefitTitle)}>
                    Never miss a draw
                  </div>
                  <div
                    className={clsx(
                      styles.benefitSubtitle,
                      'aboutUsTypographyBodySmall',
                    )}
                  >
                    Automatic entry each week, cancel anytime
                  </div>
                </div>
              </div>
              <div className={styles.benefit}>
                <TealCircleCrown width={60} />
                <div className={styles.benefitText}>
                  <div className={clsx(styles.benefitTitle)}>
                    Exclusive raffles
                  </div>
                  <div
                    className={clsx(
                      styles.benefitSubtitle,
                      'aboutUsTypographyBodySmall',
                    )}
                  >
                    Even more ways to win
                  </div>
                </div>
              </div>
              <div className={styles.benefit}>
                <TealCircleTrophy width={60} />
                <div className={styles.benefitText}>
                  <div className={clsx(styles.benefitTitle)}>
                    Leaderboard bonus
                  </div>
                  <div
                    className={clsx(
                      styles.benefitSubtitle,
                      'aboutUsTypographyBodySmall',
                    )}
                  >
                    Bonus points every month
                  </div>
                </div>
              </div>
            </div>
            <p>
              <RoundedButton
                href="/enternow/1"
                rebrandDm2025
                fullWidth
                onClick={() => {
                  enterStore.setEntryMethod(EntryMethod.PremiumSubscription);
                  if (isBoosted) {
                    enterStore.setBoosted(true);
                  }
                }}
              >
                Play from {premiumPrice}
              </RoundedButton>
            </p>
          </div>
        </AboutUsSplitHero>
      </AboutUsModule>

      <AboutUsModule background="tealSolid">
        <AboutUsSiteContainer>
          <AboutUsSiteInnerContainer>
            <div className={styles.header}>
              <div
                className={clsx(styles.title, 'aboutUsTypographyHeaderLarge')}
              >
                How it works
              </div>
            </div>
            <div className={styles.howItWorks}>
              <EmblaCarousel
                className={styles.carousel}
                emblaCarouselInfo={emblaCarouselInfo}
                hidePips={isMobile ? false : true}
                items={[
                  <div className={styles.howItWorksStep}>
                    <WhiteShadowArrow width={24} />
                    <div
                      className={clsx(
                        styles.stepTitle,
                        'aboutUsTypographyHeaderMedium',
                      )}
                    >
                      Pick your numbers
                    </div>
                    <div className={styles.stepSubtitle}>
                      Choose from our paid or free weekly membership options and
                      then select 5 lucky numbers.
                    </div>
                  </div>,
                  <div className={styles.howItWorksStep}>
                    <WhiteShadowMatch width={24} />
                    <div
                      className={clsx(
                        styles.stepTitle,
                        'aboutUsTypographyHeaderMedium',
                      )}
                    >
                      Match to win
                    </div>
                    <div className={styles.stepSubtitle}>
                      The winning numbers will be randomly drawn every Tuesday &
                      Friday at 5pm.
                    </div>
                  </div>,
                  <div className={styles.howItWorksStep}>
                    <WhiteShadowTrophy width={24} />
                    <div
                      className={clsx(
                        styles.stepTitle,
                        'aboutUsTypographyHeaderMedium',
                      )}
                    >
                      Choose your prize
                    </div>
                    <div className={styles.stepSubtitle}>
                      The more numbers you match, the bigger the prize you get
                      to choose.
                    </div>
                  </div>,
                ]}
              ></EmblaCarousel>
            </div>
          </AboutUsSiteInnerContainer>
        </AboutUsSiteContainer>
      </AboutUsModule>

      <AboutUsModule background="white" withNoPadding>
        <div className={styles.winnerImages}>
          <div className={styles.winnerImage}>
            <img
              src="https://daymade-website-assets.s3.amazonaws.com/2026/choose-your-plan/image1.png"
              alt="DAYMADE winner"
            />
          </div>
          <div className={styles.winnerImage}>
            <img
              src="https://daymade-website-assets.s3.amazonaws.com/2026/choose-your-plan/image5.png"
              alt="DAYMADE winner"
            />
          </div>

          <div className={styles.winnerImage}>
            <img
              src="https://daymade-website-assets.s3.amazonaws.com/2026/choose-your-plan/image3.png"
              alt="DAYMADE winner"
            />
          </div>

          <div className={styles.winnerImage}>
            <img
              src="https://daymade-website-assets.s3.amazonaws.com/2026/choose-your-plan/image4.png"
              alt="DAYMADE winner"
            />
          </div>
        </div>
      </AboutUsModule>

      <AboutUsModule background="white">
        <AboutUsSiteContainer>
          <h2
            className={clsx(
              styles.faqTitle,
              'aboutUsTypographyHeaderXlargeOnWhite',
            )}
            data-text="FAQ"
          >
            FAQ
          </h2>
          {faqs.map((x, index) => (
            <FaqCard
              key={index}
              rebrand2025
              question={x.question}
              answer={x.answer}
              withBorder
            ></FaqCard>
          ))}
        </AboutUsSiteContainer>
      </AboutUsModule>
    </div>
  );
};

const DummyComponent = () => <div></div>;
export default DummyComponent;
export async function getServerSideProps() {
  return {
    // This AB test page shouldn't serve content. If the winner is chosen, then we rename this page to choose-your-plan
    notFound: true,
  };
}
